import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { CloudUploadIcon, PhotographIcon, XIcon } from "@heroicons/react/outline";
import type { IMedia } from "src/model/unityObject";
import { useMediaPicker } from "../picker/useMediaPicker";

const BlockadeImageToImage = ({
  onSelect,
  onCancel,
  imageKind = "360",
}: {
  onSelect: (value: File | string) => void;
  onCancel: () => void;
  imageKind?: "360" | "2d";
}) => {
  const { t } = useTranslation();
  const { pickMediaOrFile, pickerElement } = useMediaPicker();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const requiredTag = imageKind === "360" ? "media360" : "media2d";

  const handleGallerySelect = (media: IMedia) => {
    setPreview(media.s3_url);
    onSelect(media.s3_url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = [".png", ".jpg", ".jpeg"];
      const fileName = file.name.toLowerCase();
      const isValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

      if (isValidExtension) {
        const fileUrl = URL.createObjectURL(file);
        setPreview(fileUrl);
        onSelect(file);
      } else {
        alert(t("general.extensionNotAcceptedImage"));
        handleCancel();
      }
    }
  };

  const handleCancel = () => {
    setPreview(null);
    onCancel();
  };

  const handlePickerFileUpload = (file: File) => {
    setPreview(URL.createObjectURL(file));
    onSelect(file);
  };

  const openGalleryPicker = async () => {
    const result = await pickMediaOrFile({
      allowedMediaTypes: "image",
      required360Tag: requiredTag,
      initialSearch: { mediaType: "image", media360tag: requiredTag, query: "", page: 0 },
      uploadAccept: "image/jpeg,image/png,image/jpg",
    });
    if (!result) return;
    if (result.kind === "media") handleGallerySelect(result.media);
    else handlePickerFileUpload(result.file);
  };

  return (
    <div>
      {preview ? (
        <div className="relative">
          <Tooltip title={t("general.cancel")}>
            <button
              onClick={handleCancel}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full shadow-md"
              aria-label={t("pages.gallery.generateMediaPanel.closePreview")}
            >
              <XIcon className="w-5 h-5 text-gray-600" strokeWidth={2} />
            </button>
          </Tooltip>
          <img
            src={preview}
            alt={t("pages.gallery.generateMediaPanel.previewImageAlt")}
            className="mb-6 w-full max-h-64 object-cover"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            type="button"
            onClick={() => void openGalleryPicker()}
            className="btn-primary-outline flex flex-col items-center gap-1 py-5"
          >
            <PhotographIcon className="w-8 h-8" />
            <span>{t("pages.gallery.generateMediaPanel.selectFromGallery")}</span>
            <span className="text-xs text-gray-500 font-normal">
              {t("pages.gallery.generateMediaPanel.selectFromGalleryHint")}
            </span>
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-primary-outline flex flex-col items-center gap-1 py-5"
          >
            <CloudUploadIcon className="w-8 h-8" />
            <span>{t("pages.gallery.generateMediaPanel.uploadFile")}</span>
            <span className="text-xs text-gray-500 font-normal">
              {t("pages.gallery.generateMediaPanel.uploadFileHint")}
            </span>
          </button>
          <input
            ref={fileInputRef}
            id="file-upload"
            type="file"
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>
      )}
      {preview && <hr className="mt-6 mb-4 w-4/5 mx-auto border-gray-300" />}

      {pickerElement}
    </div>
  );
};

export default BlockadeImageToImage;
